import { Injectable, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private translate = inject(TranslateService);

  public currentLang = signal<string>('en');
  public supportedLanguages = signal<string[]>(['en']);

  constructor() {
    this.translate.addLangs(['en']);
    this.translate.setFallbackLang('en');

    // Try to recover saved language without validating against supportedLanguages yet (as they are not loaded)
    const savedLang = localStorage.getItem('app_lang');
    const browserLang = this.translate.getBrowserLang();
    const initialLang = savedLang || browserLang || 'en';

    // Set initial language but DO NOT overwrite localStorage yet
    this.translate.use(initialLang);
    this.currentLang.set(initialLang);
  }

  init(languages: string[] = [], defaultLang: string = 'en') {
    if (!Array.isArray(languages) || languages.length === 0) {
      console.warn(
        'LanguageService: No languages provided, using default "en"'
      );
      languages = ['en'];
    }

    this.supportedLanguages.set(languages);
    this.translate.addLangs(languages);
    this.translate.setFallbackLang(defaultLang);

    const preferredLang = this.getPreferredLanguage(languages, defaultLang);

    if (preferredLang !== this.currentLang()) {
      this.use(preferredLang);
    } else {
      // Ensure we save the confirmed language to localStorage (in case it wasn't there or was different)
      // But only if it's valid.
      this.use(preferredLang);
    }
  }

  use(lang: string) {
    this.translate.use(lang);
    this.currentLang.set(lang);
    localStorage.setItem('app_lang', lang);
  }

  getCountryCodeForLanguage(langCode: string): string {
    const codeMap: Record<string, string> = {
      en: 'US',
      uk: 'UA',
      ru: 'RU',
      de: 'DE',
      fr: 'FR',
      es: 'ES',
      it: 'IT',
      pl: 'PL',
      cs: 'CZ',
      pt: 'PT',
      ja: 'JP',
      zh: 'CN',
      tr: 'TR',
      nl: 'NL',
      sv: 'SE',
      da: 'DK',
      fi: 'FI',
      no: 'NO',
      ko: 'KR',
      he: 'IL',
      ar: 'SA',
      hi: 'IN',
      bg: 'BG',
      ro: 'RO',
      sk: 'SK',
      sl: 'SI',
      hr: 'HR',
      sr: 'RS',
      hu: 'HU',
      el: 'GR',
      lt: 'LT',
      lv: 'LV',
      et: 'EE',
      th: 'TH',
      vi: 'VN',
      id: 'ID',
      ms: 'MY',
      kk: 'KZ',
      ka: 'GE'
    };

    return codeMap[langCode.toLowerCase()] || langCode;
  }

  getLanguageName(langCode: string): string {
    const namesMap: Record<string, string> = {
      en: 'English',
      fr: 'Français',
      uk: 'Українська',
      ru: 'Русский',
      pl: 'Polski',
      sk: 'Slovak',
      hy: 'Հայերեն',
      ka: 'ქართული',
      kk: 'Қазақша',
      uz: "O'zbekcha",
      az: 'Азербайджандық',
      de: 'Deutsch',
      es: 'Español',
      it: 'Italiano',
      cs: 'Čeština',
      pt: 'Português',
      ja: '日本語',
      zh: '中文',
      tr: 'Türkçe',
      nl: 'Nederlands',
      sv: 'Svenska',
      da: 'Dansk',
      fi: 'Suomi',
      no: 'Norsk',
      ko: '한국어',
      he: 'עברית',
      ar: 'العربية',
      hi: 'हिन्दी',
      bg: 'Български',
      ro: 'Română',
      sl: 'Slovenščina',
      hr: 'Hrvatski',
      sr: 'Српски',
      hu: 'Magyar',
      el: 'Ελληνικά',
      lt: 'Lietuvių',
      lv: 'Latviešu',
      et: 'Eesti',
      th: 'ไทย',
      vi: 'Tiếng Việt',
      id: 'Bahasa Indonesia',
      ms: 'Bahasa Melayu'
    };

    return namesMap[langCode.toLowerCase()] || langCode.toUpperCase();
  }

  private getPreferredLanguage(
    supportedLanguages: string[],
    defaultLang: string
  ): string {
    // 1. Check saved user language
    const savedLang = localStorage.getItem('app_lang');
    if (savedLang && supportedLanguages.includes(savedLang)) {
      return savedLang;
    }

    // 2. Check browser/device language
    const browserLang = this.translate.getBrowserLang();
    if (browserLang && supportedLanguages.includes(browserLang)) {
      return browserLang;
    }

    // 3. Return default language
    return defaultLang;
  }
}
